"use client";

import { useState, useCallback, useEffect, startTransition, useRef } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { VehicleUpload } from "@/components/app/vehicle-upload";
import { VehicleHistory } from "@/components/app/vehicle-history";
import { RecentVehicles } from "@/components/app/recent-vehicles";
import { FeaturedWheelPicker } from "@/components/app/featured-wheel-picker";
import { RimSelector } from "@/components/app/rim-selector";
import { GenerationControls } from "@/components/app/generation-controls";
import { LoadingVisualAudit } from "@/components/app/loading-visual-audit";
import { ResultsView } from "@/components/app/results-view";
import { FullscreenResultModal } from "@/components/app/fullscreen-result-modal";
import { FreePreviewComparison } from "@/components/app/free-preview-comparison";
import { StickyGenerateCTA } from "@/components/app/sticky-generate-cta";
import { AppHeader } from "@/components/app/app-header";
import { useSession } from "@/lib/auth/client";
import {
  findCheckoutRestoreCandidate,
  FREE_PREVIEW_CHECKOUT_SELECTION_KEY,
  getFreePreviewComparisonCandidates,
} from "@/lib/free-preview-funnel";
import { useCredits, useInvalidateCredits } from "@/hooks/use-credits";
import { useUpload } from "@/hooks/use-upload";
import { useVehicleUploads } from "@/hooks/use-vehicle-uploads";
import {
  useCreateGeneration,
  useGeneration,
  useUnlockGeneration,
} from "@/hooks/use-generation";
import { useGenerations, type GenerationWithVehicle } from "@/hooks/use-generations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn, sanitizeErrorMessage } from "@/lib/utils";
import {
  canUseFreePreviewForUser,
  freePreviewEligibilityFromCredits,
} from "@/lib/payments/free-preview-config";
import {
  trackAppPageViewed,
  trackWheelSelected,
  trackWheelDeselected,
  trackFreePreviewStarted,
  trackFreePreviewCompleted,
  trackGenerationStarted,
  trackGenerationCompleted,
  identifyUser,
  trackUnlockClicked,
} from "@/lib/posthog-client";

// Fixed credit cost per HD preview (all generations use 1K resolution)
const CREDIT_COST = 1;

export default function AppPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: session, isPending: sessionLoading } = useSession();
  const {
    data: credits,
    isSuccess: creditsSuccess,
    refetch: refetchCredits,
  } = useCredits();
  const invalidateCredits = useInvalidateCredits();
  const timeoutIdsRef = useRef<number[]>([]);
  const isMountedRef = useRef(false);

  // Refs for auto-scroll (mobile)
  const wheelSectionRef = useRef<HTMLDivElement>(null);
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  const loadingSectionRef = useRef<HTMLDivElement>(null);

  // Car state
  const [vehicleUploadId, setVehicleUploadId] = useState<string | null>(null);
  const [vehiclePreviewUrl, setVehiclePreviewUrl] = useState<string | null>(null);
  const [showUploadCard, setShowUploadCard] = useState(true);
  const { upload: uploadVehicle, isUploading: vehicleUploading } = useUpload();
  const { data: vehicleUploadsData, isSuccess: vehicleUploadsLoaded } = useVehicleUploads();
  const hasVehiclesInGarage = (vehicleUploadsData?.uploads?.length || 0) > 0;
  const hasAutoSelectedVehicleRef = useRef(false);

  // Rim state
  const [selectedRimId, setSelectedRimId] = useState<string | null>(null);
  const [selectedUserRimId, setSelectedUserRimId] = useState<string | null>(null);

  // Returning users should arrive ready to choose a wheel, not at an empty
  // uploader. The API returns uploads newest-first; this only runs once so a
  // later user choice is never overwritten.
  useEffect(() => {
    if (!vehicleUploadsLoaded || hasAutoSelectedVehicleRef.current) return;

    hasAutoSelectedVehicleRef.current = true;
    const mostRecentVehicle = vehicleUploadsData?.uploads.find((upload) => upload.imageUrl);
    if (mostRecentVehicle && !vehicleUploadId) {
      queueMicrotask(() => {
        if (!isMountedRef.current) return;
        setVehicleUploadId(mostRecentVehicle.id);
        setVehiclePreviewUrl(mostRecentVehicle.imageUrl);
        setShowUploadCard(false);
      });
    }
  }, [vehicleUploadsData, vehicleUploadsLoaded, vehicleUploadId]);
  
  // Fetch generations to check if user has any past generations
  const {
    data: generationsData,
    isSuccess: generationsSuccess,
    refetch: refetchGenerations,
  } = useGenerations();
  const hasPastGenerations = (generationsData?.generations || []).some(
    (g) => g.status === "completed" && g.resultImageUrl
  );

  // Generation state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentGenerationId, setCurrentGenerationId] = useState<string | null>(null);
  const [viewingPastGeneration, setViewingPastGeneration] = useState<GenerationWithVehicle | null>(null);
  const [showFullscreenResult, setShowFullscreenResult] = useState(false);
  const [showFreePreviewComparison, setShowFreePreviewComparison] = useState(false);
  const [checkoutReturnGenerationId, setCheckoutReturnGenerationId] = useState<string | null>(null);
  const [requestedGenerationId, setRequestedGenerationId] = useState<string | null>(null);
  const createGeneration = useCreateGeneration();
  const generationQuery = useGeneration(currentGenerationId);
  const generation = generationQuery.data;
  const unlockGeneration = useUnlockGeneration();

  // Check eligibility for free preview (server counts via /api/credits)
  const isCompanyMember = credits?.isCompanyMember ?? false;
  const freePreviewEligibility = freePreviewEligibilityFromCredits(credits);
  const remainingFreePreviews = credits?.freePreviewsRemaining ?? 0;
  const isEligibleForFreePreview = canUseFreePreviewForUser(freePreviewEligibility);

  // Track completion to invalidate queries once when generation finishes
  // Using ref instead of state to avoid cascading renders - this value is only
  // used for deduplication, not for rendering
  const lastCompletedIdRef = useRef<string | null>(null);
  const lastFailedIdRef = useRef<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    isMountedRef.current = true;
    startTransition(() => {
      setIsMounted(true);
    });
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const scheduleTimeout = useCallback((callback: () => void, delayMs: number) => {
    const timeoutId = window.setTimeout(callback, delayMs);
    timeoutIdsRef.current.push(timeoutId);
  }, []);

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      setShowConfirmDialog(false);
      setShowFullscreenResult(false);
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  useEffect(() => {
    if (!session?.user || !creditsSuccess) return;

    identifyUser(session.user.id, {
      email: session.user.email,
      name: session.user.name,
      hasPurchasedAnyPack: credits?.hasPurchasedAnyPack,
      creditBalance: credits?.balance,
      isCompanyMember: credits?.isCompanyMember,
    });
  }, [
    session?.user,
    creditsSuccess,
    credits?.hasPurchasedAnyPack,
    credits?.balance,
    credits?.isCompanyMember,
  ]);
  
  // Track generation completion
  // Using ref for lastCompletedId to avoid synchronous setState warnings
  // and prevent cascading renders. The ref update + invalidations are side effects,
  // while setShowFullscreenResult is scheduled to run after via queueMicrotask
  useEffect(() => {
    if (
      generation?.status === "completed" &&
      generation.resultImageUrl &&
      generation.id !== lastCompletedIdRef.current &&
      !viewingPastGeneration
    ) {
      // Update ref immediately to prevent duplicate processing
      lastCompletedIdRef.current = generation.id;
      
      // Invalidate queries (these don't cause React re-renders directly)
      queryClient.invalidateQueries({ queryKey: ["generations"] });
      invalidateCredits();
      
      // Track analytics
      const durationMs = generation.completedAt && generation.createdAt
        ? new Date(generation.completedAt).getTime() - new Date(generation.createdAt).getTime()
        : undefined;

      if (generation.isFreePreview) {
        trackFreePreviewCompleted({ generationId: generation.id, durationMs });
      } else {
        trackGenerationCompleted({ generationId: generation.id, durationMs });
      }
      
      // Schedule state update to show fullscreen modal after effect completes
      // This avoids the "setState synchronously within an effect" warning
      const hasEarlierCompletedFreePreviewForVehicle = generation.isFreePreview &&
        (generationsData?.generations || []).some((candidate) =>
          candidate.id !== generation.id &&
          candidate.vehicleUploadId === generation.vehicleUploadId &&
          candidate.isFreePreview &&
          candidate.status === "completed" &&
          candidate.resultImageUrl
        );

      queueMicrotask(() => {
        if (isMountedRef.current) {
          setShowFreePreviewComparison(!!hasEarlierCompletedFreePreviewForVehicle);
          setShowFullscreenResult(!hasEarlierCompletedFreePreviewForVehicle);
        }
      });
    }
  }, [
    generation?.status,
    generation?.resultImageUrl,
    generation?.id,
    generation?.completedAt,
    generation?.createdAt,
    generation?.isFreePreview,
    generation?.vehicleUploadId,
    viewingPastGeneration,
    queryClient,
    invalidateCredits,
    generationsData?.generations,
  ]);

  // Surface async/background generation failures (e.g. the Inngest job fails
  // after the initial request already succeeded) with a toast, since the
  // user may have scrolled away from the inline error card by then.
  useEffect(() => {
    if (
      generation?.status === "failed" &&
      generation.id !== lastFailedIdRef.current &&
      !viewingPastGeneration
    ) {
      lastFailedIdRef.current = generation.id;
      toast.error(
        sanitizeErrorMessage(generation.errorMessage) ||
          "Generation failed. Please try again."
      );
    }
  }, [generation?.status, generation?.id, generation?.errorMessage, viewingPastGeneration]);

  // Handle car file selection
  const handleVehicleFileSelect = useCallback(
    async (file: File) => {
      const blobUrl = URL.createObjectURL(file);
      setVehiclePreviewUrl(blobUrl);
      setShowUploadCard(false);

      const result = await uploadVehicle(file, "vehicles");
      if (result?.vehicleUploadId) {
        setVehicleUploadId(result.vehicleUploadId);
        if (result.publicUrl) {
          setVehiclePreviewUrl(result.publicUrl);
          URL.revokeObjectURL(blobUrl);
        }
        // Auto-scroll on mobile
        scheduleTimeout(() => {
          wheelSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      } else {
        // Upload failed: clear optimistic preview to avoid a misleading "uploaded" state.
        setVehiclePreviewUrl(null);
        setVehicleUploadId(null);
        setShowUploadCard(true);
        URL.revokeObjectURL(blobUrl);
      }
    },
    [uploadVehicle, scheduleTimeout]
  );

  // Handle selecting from car history
  const handleSelectFromHistory = useCallback((uploadId: string, imageUrl: string) => {
    setVehicleUploadId(uploadId);
    setVehiclePreviewUrl(imageUrl);
    setShowUploadCard(false);
    scheduleTimeout(() => {
      wheelSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, [scheduleTimeout]);

  // Handle upload new car
  const handleUploadNew = useCallback(() => {
    setVehicleUploadId(null);
    setVehiclePreviewUrl(null);
    setShowUploadCard(true);
    scheduleTimeout(() => {
      uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [scheduleTimeout]);

  // Handle rim selection
  const handleSelectRim = useCallback((rimId: string | null, userRimId: string | null) => {
    if (rimId || userRimId) {
      trackWheelSelected({
        wheelType: userRimId ? "custom" : "catalog",
        wheelId: (userRimId || rimId)!,
      });
    } else if (selectedRimId || selectedUserRimId) {
      trackWheelDeselected();
    }
    setSelectedRimId(rimId);
    setSelectedUserRimId(userRimId);
  }, [selectedRimId, selectedUserRimId]);

  // Handle generate click
  const handleGenerateClick = () => {
    setShowConfirmDialog(true);
  };

  // Handle free preview generation
  const handleGenerateFreePreview = async () => {
    if (!vehicleUploadId || createGeneration.isPending) return;

    trackFreePreviewStarted({
      vehicleUploadId,
      wheelId: selectedRimId || selectedUserRimId || undefined,
      wheelType: selectedUserRimId ? "custom" : "catalog",
    });

    // Scroll to loading section on mobile
    scheduleTimeout(() => {
      loadingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    try {
      const result = await createGeneration.mutateAsync({
        vehicleUploadId: vehicleUploadId!,
        rimId: selectedRimId || undefined,
        userRimId: selectedUserRimId || undefined,
        resolution: "low",
        isFreePreview: true,
      });
      setViewingPastGeneration(null);
      setCurrentGenerationId(result.id);
    } catch (error) {
      console.error("Free preview generation failed:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Something went wrong starting your free preview. Please try again."
      );
    }
  };

  // Helper to extract wheel info
  const getWheelInfo = useCallback((gen: GenerationWithVehicle | null) => {
    if (!gen) return undefined;
    if (gen.userRim) {
      return {
        name: gen.userRim.name,
        imageUrl: gen.userRim.imageUrl,
        brand: gen.userRim.brand,
        sku: gen.userRim.sku,
        isCustom: true,
      };
    }
    if (gen.rim) {
      return {
        name: gen.rim.name,
        imageUrl: gen.rim.imageUrl,
        isCustom: false,
      };
    }
    return null;
  }, []);

  const handleConfirmGeneration = async (isFreePreview = false) => {
    if (!vehicleUploadId || createGeneration.isPending) return;
    setShowConfirmDialog(false);

    trackGenerationStarted({
      vehicleUploadId,
      wheelId: selectedRimId || selectedUserRimId || undefined,
      wheelType: selectedUserRimId ? "custom" : "catalog",
      creditCost: CREDIT_COST,
    });

    // Scroll to loading section on mobile
    scheduleTimeout(() => {
      loadingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    try {
      const result = await createGeneration.mutateAsync({
        vehicleUploadId: vehicleUploadId!,
        rimId: selectedRimId || undefined,
        userRimId: selectedUserRimId || undefined,
        resolution: "low",
        isFreePreview,
      });
      setViewingPastGeneration(null);
      setCurrentGenerationId(result.id);
    } catch (error) {
      console.error("Generation failed:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Something went wrong starting your generation. Please try again."
      );
    }
  };

  // Handle unlock
  const handleUnlock = async () => {
    const currentGen = viewingPastGeneration || generation;
    const genId = currentGen?.id || currentGenerationId;
    
    if (!genId) {
      toast.error("No generation selected");
      return;
    }

    try {
      const result = await unlockGeneration.mutateAsync(genId);
      toast.success("Preview unlocked!");
      
      if (viewingPastGeneration) {
        queryClient.invalidateQueries({ queryKey: ["generations"] });
        setViewingPastGeneration({
          ...viewingPastGeneration,
          isUnlocked: true,
          resultImageUrl: result.resultImageUrl || result.originalImageUrl || viewingPastGeneration.resultImageUrl,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["generation", genId] });
      }
      queryClient.invalidateQueries({ queryKey: ["generations"] });
      invalidateCredits();
    } catch (error) {
      console.error("Unlock failed:", error);
      toast.error(error instanceof Error ? error.message : "Failed to unlock preview");
    }
  };

  const handleUnlockComparisonPreview = useCallback(async (selectedGeneration: GenerationWithVehicle) => {
    try {
      trackUnlockClicked({
        generationId: selectedGeneration.id,
        creditBalance: credits?.balance || 0,
      });
      const unlocked = await unlockGeneration.mutateAsync(selectedGeneration.id);
      setShowFreePreviewComparison(false);
      setViewingPastGeneration({
        ...selectedGeneration,
        ...unlocked,
        isUnlocked: true,
      });
      setCurrentGenerationId(null);
      setShowFullscreenResult(true);
      invalidateCredits();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to unlock preview");
    }
  }, [unlockGeneration, invalidateCredits, credits]);

  // Handle try another
  const handleTryAnother = useCallback(() => {
    setCurrentGenerationId(null);
    setViewingPastGeneration(null);
    setShowFullscreenResult(false);
    invalidateCredits();
    queryClient.invalidateQueries({ queryKey: ["generations"] });
  }, [invalidateCredits, queryClient]);

  // Handle selecting a past generation
  const handleSelectPastGeneration = useCallback((gen: GenerationWithVehicle) => {
    setViewingPastGeneration(gen);
    setCurrentGenerationId(null);
    setShowFullscreenResult(true);
    if (gen.vehicleImageUrl) {
      setVehiclePreviewUrl(gen.vehicleImageUrl);
      setVehicleUploadId(gen.vehicleUploadId);
      setShowUploadCard(false);
    }
  }, []);

  const hasRestoredCheckoutSelectionRef = useRef(false);
  const checkoutPollAttemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryGenerationId = params.get("generationId");

    if (params.get("success") !== "true") {
      if (queryGenerationId) {
        queueMicrotask(() => {
          if (isMountedRef.current) {
            setRequestedGenerationId(queryGenerationId);
          }
        });
      }
      return;
    }

    let storedGenerationId: string | null = null;
    try {
      storedGenerationId = window.sessionStorage.getItem(FREE_PREVIEW_CHECKOUT_SELECTION_KEY);
    } catch {
      // The query parameter is the fallback when browser storage is unavailable.
    }

    const selectedGenerationId = queryGenerationId || storedGenerationId;
    if (!selectedGenerationId) return;

    queueMicrotask(() => {
      if (isMountedRef.current) {
        setCheckoutReturnGenerationId(selectedGenerationId);
      }
    });
    toast.loading("Payment received. Unlocking your selected preview…", {
      id: "checkout-result-unlock",
    });
  }, []);

  useEffect(() => {
    if (!requestedGenerationId || !generationsSuccess) return;

    const requestedGeneration = (generationsData?.generations || []).find(
      (candidate) => candidate.id === requestedGenerationId
    );
    if (!requestedGeneration) return;

    queueMicrotask(() => {
      if (!isMountedRef.current) return;
      setViewingPastGeneration(requestedGeneration);
      setCurrentGenerationId(null);
      setShowFreePreviewComparison(false);
      setShowFullscreenResult(true);
      setRequestedGenerationId(null);
      router.replace("/app");
    });
  }, [generationsData?.generations, generationsSuccess, requestedGenerationId, router]);

  useEffect(() => {
    if (!checkoutReturnGenerationId || (credits?.balance || 0) >= 1) return;

    checkoutPollAttemptsRef.current = 0;
    const intervalId = window.setInterval(() => {
      checkoutPollAttemptsRef.current += 1;
      void Promise.all([refetchCredits(), refetchGenerations()]);

      if (checkoutPollAttemptsRef.current >= 15) {
        window.clearInterval(intervalId);
        toast.info("Your payment is still processing. Your selected preview is saved.", {
          id: "checkout-result-unlock",
        });
      }
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [checkoutReturnGenerationId, credits?.balance, refetchCredits, refetchGenerations]);

  useEffect(() => {
    if (
      !checkoutReturnGenerationId ||
      hasRestoredCheckoutSelectionRef.current ||
      !creditsSuccess ||
      !generationsSuccess
    ) {
      return;
    }

    const selectedGeneration = (generationsData?.generations || []).find(
      (candidate) => candidate.id === checkoutReturnGenerationId
    );

    if (!selectedGeneration) return;

    if (selectedGeneration.isUnlocked) {
      hasRestoredCheckoutSelectionRef.current = true;
      try {
        window.sessionStorage.removeItem(FREE_PREVIEW_CHECKOUT_SELECTION_KEY);
      } catch {
        // The result can still be restored when storage cleanup is unavailable.
      }
      queueMicrotask(() => {
        if (!isMountedRef.current) return;
        setViewingPastGeneration(selectedGeneration);
        setCurrentGenerationId(null);
        setShowFreePreviewComparison(false);
        setShowFullscreenResult(true);
        setCheckoutReturnGenerationId(null);
        router.replace("/app");
        toast.success("Your preview is ready to download.", {
          id: "checkout-result-unlock",
        });
      });
      return;
    }

    const restoreCandidate = findCheckoutRestoreCandidate({
      generations: generationsData?.generations || [],
      selectedGenerationId: checkoutReturnGenerationId,
      creditBalance: credits?.balance || 0,
    });
    if (!restoreCandidate) return;

    hasRestoredCheckoutSelectionRef.current = true;
    unlockGeneration.mutate(restoreCandidate.id, {
      onSuccess: (unlocked) => {
        if (!isMountedRef.current) return;

        try {
          window.sessionStorage.removeItem(FREE_PREVIEW_CHECKOUT_SELECTION_KEY);
        } catch {
          // The return flow is still complete when storage cleanup is unavailable.
        }

        setViewingPastGeneration({
          ...restoreCandidate,
          ...unlocked,
          isUnlocked: true,
          resultImageUrl:
            unlocked.resultImageUrl ||
            unlocked.originalImageUrl ||
            restoreCandidate.resultImageUrl,
        });
        setCurrentGenerationId(null);
        setShowFreePreviewComparison(false);
        setShowFullscreenResult(true);
        setCheckoutReturnGenerationId(null);
        router.replace("/app");
        invalidateCredits();
        toast.success("Preview unlocked and ready to download.", {
          id: "checkout-result-unlock",
        });
      },
      onError: (error) => {
        hasRestoredCheckoutSelectionRef.current = false;
        setViewingPastGeneration(restoreCandidate);
        setCurrentGenerationId(null);
        setShowFreePreviewComparison(false);
        setShowFullscreenResult(true);
        toast.error(
          error instanceof Error
            ? `${error.message}. Tap Unlock and Download to retry.`
            : "Could not unlock automatically. Tap Unlock and Download to retry.",
          { id: "checkout-result-unlock" }
        );
      },
    });
  }, [
    checkoutReturnGenerationId,
    credits?.balance,
    creditsSuccess,
    generationsData?.generations,
    generationsSuccess,
    invalidateCredits,
    router,
    unlockGeneration,
  ]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!sessionLoading && !session?.user) {
      router.push("/");
    }
  }, [sessionLoading, session?.user, router]);

  // Track app page view
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (creditsSuccess && generationsSuccess && session?.user && !hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackAppPageViewed({
        hasExistingVehicle: !!vehicleUploadId,
        hasExistingGenerations: hasPastGenerations,
        isEligibleForFreePreview,
        creditBalance: credits?.balance ?? 0,
      });
    }
  }, [creditsSuccess, generationsSuccess, session?.user, vehicleUploadId, hasPastGenerations, isEligibleForFreePreview, credits?.balance]);

  // Loading state
  if (!isMounted || sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session?.user) {
    return null;
  }

  // Computed values
  const hasPendingGeneration = (generationsData?.generations || []).some(
    (g) => g.status === "pending" || g.status === "processing"
  );
  const isGenerating = createGeneration.isPending || hasPendingGeneration || (currentGenerationId && generation?.status !== "completed" && generation?.status !== "failed");
  const displayedResultUrl = isGenerating ? null : (viewingPastGeneration?.resultImageUrl || generation?.resultImageUrl);
  const currentGen = viewingPastGeneration || generation || (hasPendingGeneration ? generationsData?.generations.find(g => g.status === "pending" || g.status === "processing") : null);
  const isFreePreview = currentGen?.isFreePreview ?? false;
  const isUnlocked = currentGen?.isUnlocked ?? true;
  const isLocked = isFreePreview && !isUnlocked;
  const hasWheel = !!(selectedRimId || selectedUserRimId);
  const hasVehicle = !!vehicleUploadId;
  const canGenerate = !!(vehicleUploadId && hasWheel && !isGenerating);
  const freePreviewComparisonCandidates = getFreePreviewComparisonCandidates({
    currentGeneration: generation,
    generations: generationsData?.generations || [],
    preferredVehicleUploadId: currentGen?.vehicleUploadId || vehicleUploadId,
  });

  // Helper to get original image URL
  const getOriginalImageUrl = (gen: GenerationWithVehicle | null | undefined): string | null => {
    if (!gen) return null;
    return gen.vehicleImageUrl;
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-3.5rem)]">
      
      {/* ============================================ */}
      {/* DESKTOP LAYOUT - Sidebar + Main Area */}
      {/* ============================================ */}
      <div className="hidden lg:flex flex-1">
        {/* Left Sidebar */}
        <div className="w-[340px] shrink-0 border-r border-white/5 p-4 space-y-5 overflow-y-auto">
          {/* Step 1: Car Photo */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                vehicleUploadId 
                  ? "bg-emerald-500 text-white" 
                  : "bg-primary text-white"
              )}>
                {vehicleUploadId ? "✓" : "1"}
              </div>
              <h2 className="text-base font-medium text-white flex-1">Car Photo</h2>
              <VehicleHistory
                selectedUploadId={vehicleUploadId}
                onSelect={handleSelectFromHistory}
                disabled={!!isGenerating}
              />
            </div>
            <VehicleUpload
              imageUrl={vehiclePreviewUrl}
              onImageChange={(url) => {
                if (isGenerating) return;
                setVehiclePreviewUrl(url);
                if (!url) setVehicleUploadId(null);
              }}
              onFileSelect={handleVehicleFileSelect}
              isUploading={vehicleUploading}
              compact
              disabled={!!isGenerating}
            />
          </div>

          <div className="h-px bg-white/10" />

          {/* Step 2: Select Wheels */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                hasWheel 
                  ? "bg-emerald-500 text-white" 
                  : vehicleUploadId 
                    ? "bg-primary text-white" 
                    : "bg-zinc-700 text-zinc-400"
              )}>
                {hasWheel ? "✓" : "2"}
              </div>
              <h2 className="text-base font-medium text-white">Select Wheels</h2>
            </div>
            <div className="h-[420px] overflow-hidden">
              <RimSelector
                selectedRimId={selectedRimId}
                selectedUserRimId={selectedUserRimId}
                onSelectRim={handleSelectRim}
                disabled={!!isGenerating || showConfirmDialog}
                hasPurchasedAnyPack={credits?.hasPurchasedAnyPack ?? false}
              />
            </div>
          </div>

          <div className="h-px bg-white/10" />

          {/* Generation Controls */}
          <GenerationControls
            onGenerate={handleGenerateClick}
            isGenerating={!!isGenerating}
            canGenerate={canGenerate}
            creditBalance={credits?.balance || 0}
            hasPurchasedAnyPack={credits?.hasPurchasedAnyPack ?? false}
            isCompanyMember={isCompanyMember}
            remainingFreePreviews={remainingFreePreviews}
            onGenerateFreePreview={isEligibleForFreePreview ? handleGenerateFreePreview : undefined}
          />
        </div>

        {/* Main Preview Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* History Button */}
          {hasPastGenerations && (
            <div className="flex justify-end mb-4">
              <AppHeader
                hasPastGenerations={hasPastGenerations}
                onSelectGeneration={handleSelectPastGeneration}
                selectedGenerationId={viewingPastGeneration?.id || currentGenerationId}
                onCompareFreePreviews={freePreviewComparisonCandidates.length >= 2 ? () => setShowFreePreviewComparison(true) : undefined}
                className="border-0 p-0 bg-transparent"
              />
            </div>
          )}

          {/* Result / Loading / Placeholder */}
          {isGenerating ? (
            <LoadingVisualAudit />
          ) : (
            <Card className={cn(displayedResultUrl && currentGen ? "py-0" : "")}>
              <CardContent className={cn("p-0", displayedResultUrl && currentGen ? "" : "p-6")}>
                {displayedResultUrl && currentGen ? (
                  <ResultsView
                    originalImageUrl={getOriginalImageUrl(currentGen) || vehiclePreviewUrl || ""}
                    resultImageUrl={displayedResultUrl}
                    onTryAnother={handleTryAnother}
                    isFreePreview={isFreePreview}
                    isUnlocked={!isLocked}
                    generationId={currentGen.id}
                    onUnlock={handleUnlock}
                    creditBalance={credits?.balance || 0}
                    hasPurchasedAnyPack={credits?.hasPurchasedAnyPack ?? false}
                    remainingFreePreviews={remainingFreePreviews}
                    onSelectGeneration={hasPastGenerations ? handleSelectPastGeneration : undefined}
                    selectedGenerationId={viewingPastGeneration?.id || currentGenerationId}
                    wheelInfo={getWheelInfo(currentGen as GenerationWithVehicle)}
                    generationDate={currentGen.completedAt ? new Date(currentGen.completedAt) : new Date(currentGen.createdAt)}
                  />
                ) : generation?.status === "failed" ? (
                  <div className="aspect-video w-full rounded-lg bg-destructive/10 flex items-center justify-center">
                    <div className="text-center text-destructive">
                      <p className="font-medium">Generation failed</p>
                      <p className="text-sm">{sanitizeErrorMessage(generation.errorMessage) || "Please try again"}</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={() => setCurrentGenerationId(null)}>
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : hasVehicle && vehiclePreviewUrl ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-zinc-950">
                    <Image
                      src={vehiclePreviewUrl}
                      alt="Your selected car"
                      fill
                      sizes="(min-width: 1024px) 70vw, 100vw"
                      className="object-contain"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/85 via-black/40 to-transparent px-6 pb-6 pt-16">
                      <p className="text-sm font-medium text-white">
                        {hasWheel ? "Your setup is ready to visualize" : "Your car is ready"}
                      </p>
                      <p className="mt-1 text-xs text-zinc-300">
                        {hasWheel ? "Generate a preview from the controls on the left." : "Choose a wheel to see it on your car."}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video w-full rounded-lg border border-dashed border-muted-foreground/25 flex items-center justify-center bg-muted/30">
                    <div className="text-center text-muted-foreground">
                      <p className="text-sm font-medium">Start with your car</p>
                      <p className="text-xs mt-1">Upload a clear side photo, then choose a wheel.</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* MOBILE LAYOUT - Full-width single page */}
      {/* ============================================ */}
      <div className="lg:hidden flex flex-col flex-1">
        {/* App Header - History button */}
        <AppHeader
          hasPastGenerations={hasPastGenerations}
          onSelectGeneration={handleSelectPastGeneration}
          selectedGenerationId={viewingPastGeneration?.id || currentGenerationId}
          onCompareFreePreviews={freePreviewComparisonCandidates.length >= 2 ? () => setShowFreePreviewComparison(true) : undefined}
        />

        {/* Main Content */}
        <div className="flex-1 px-4 pb-40 pt-2 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-linear-to-br from-blue-500/10 via-zinc-950 to-zinc-950 p-4 shadow-[0_16px_45px_rgba(0,0,0,0.22)]">
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-300">Build your preview</p>
            <p className="mb-4 text-sm text-zinc-400">Start with a car photo, then find the wheel that fits.</p>

            {/* Step 1: Car selection */}
            <div ref={uploadSectionRef}>
            {/* Step header */}
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                vehicleUploadId 
                  ? "bg-emerald-500 text-white" 
                  : "bg-primary text-white"
              )}>
                {vehicleUploadId ? "✓" : "1"}
              </div>
              <h2 className="text-base font-semibold text-white">{vehicleUploadId && !showUploadCard ? "Your car is ready" : "Choose your car"}</h2>
            </div>

            {vehicleUploadId && vehiclePreviewUrl && !showUploadCard ? (
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-zinc-900">
                  <Image src={vehiclePreviewUrl} alt="Selected car" fill sizes="80px" className="object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white">Car selected</p>
                  <p className="mt-0.5 text-xs text-zinc-400">Choose a wheel, then generate your preview.</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleUploadNew} disabled={!!isGenerating}>
                  Change
                </Button>
              </div>
            ) : (
              <VehicleUpload
                imageUrl={vehiclePreviewUrl}
                onImageChange={(url) => {
                  if (isGenerating) return;
                  setVehiclePreviewUrl(url);
                  if (!url) {
                    setVehicleUploadId(null);
                    setShowUploadCard(true);
                  }
                }}
                onFileSelect={handleVehicleFileSelect}
                isUploading={vehicleUploading}
                compact
                disabled={!!isGenerating}
              />
            )}
            </div>

            <div className="my-4 h-px bg-white/10" />

            <RecentVehicles
              selectedUploadId={vehicleUploadId}
              onSelect={handleSelectFromHistory}
              onFileSelect={handleVehicleFileSelect}
              disabled={!!isGenerating}
            />
          </div>

          {/* Step 2: Wheel Picker */}
          <div ref={wheelSectionRef} className="scroll-mt-4 rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            {/* Step header */}
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                hasWheel && hasVehicle
                  ? "bg-emerald-500 text-white" 
                  : hasWheel || vehicleUploadId
                    ? "bg-primary text-white" 
                    : "bg-zinc-700 text-zinc-400"
              )}>
                {hasWheel && hasVehicle ? "✓" : "2"}
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Pick your wheel style</h2>
                <p className="mt-0.5 text-xs text-zinc-500">
                  {hasWheel
                    ? hasVehicle
                      ? "Wheel selected — your setup is ready."
                      : "Wheel selected — choose a car to visualize it."
                    : "Browse our most popular styles first."}
                </p>
              </div>
            </div>
            <FeaturedWheelPicker
              selectedRimId={selectedRimId}
              selectedUserRimId={selectedUserRimId}
              onSelectRim={handleSelectRim}
            disabled={!!isGenerating || showConfirmDialog}
              hasPurchasedAnyPack={credits?.hasPurchasedAnyPack ?? false}
            />
          </div>

          {/* Loading State - No card wrapper */}
          {isGenerating && (
            <div ref={loadingSectionRef} className="rounded-xl overflow-hidden scroll-mt-4">
              <LoadingVisualAudit />
            </div>
          )}

          {/* Generation Failed State */}
          {generation?.status === "failed" && !isGenerating && (
            <div className="rounded-xl bg-destructive/10 p-6">
              <div className="text-center text-destructive">
                <p className="font-medium">Generation failed</p>
                <p className="text-sm mt-1">{sanitizeErrorMessage(generation.errorMessage) || "Please try again"}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setCurrentGenerationId(null)}>
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Generate CTA - Mobile only */}
        <StickyGenerateCTA
          onGenerate={handleGenerateClick}
          onGenerateFreePreview={isEligibleForFreePreview ? handleGenerateFreePreview : undefined}
          isGenerating={!!isGenerating}
          canGenerate={canGenerate}
          creditBalance={credits?.balance || 0}
          isEligibleForFreePreview={isEligibleForFreePreview}
          hasVehicle={hasVehicle}
          hasWheel={hasWheel}
          hasVehiclesInGarage={hasVehiclesInGarage}
          vehicleImageUrl={vehiclePreviewUrl}
        />

        {/* Fullscreen Result Modal - Mobile only */}
        <FullscreenResultModal
          isOpen={showFullscreenResult && !!displayedResultUrl}
          onClose={() => setShowFullscreenResult(false)}
          resultImageUrl={displayedResultUrl || ""}
          onTryAnother={handleTryAnother}
          isFreePreview={isFreePreview}
          isUnlocked={!isLocked}
          generationId={currentGen?.id}
          onUnlock={handleUnlock}
          creditBalance={credits?.balance || 0}
          hasPurchasedAnyPack={credits?.hasPurchasedAnyPack ?? false}
          remainingFreePreviews={remainingFreePreviews}
          wheelInfo={getWheelInfo(currentGen as GenerationWithVehicle)}
          generationDate={currentGen?.completedAt ? new Date(currentGen.completedAt) : currentGen?.createdAt ? new Date(currentGen.createdAt) : null}
        />
      </div>

      <FreePreviewComparison
        open={showFreePreviewComparison}
        onOpenChange={setShowFreePreviewComparison}
        previews={freePreviewComparisonCandidates}
        onOpenPreview={(selectedGeneration) => {
          setShowFreePreviewComparison(false);
          handleSelectPastGeneration(selectedGeneration);
        }}
        creditBalance={credits?.balance || 0}
        isUnlocking={unlockGeneration.isPending}
        onUnlockPreview={handleUnlockComparisonPreview}
      />

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate HD Preview</DialogTitle>
            <DialogDescription>
              Your wheel preview will be ready in seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Credits used:</span>
              <span className="font-medium flex items-center gap-1">
                <Coins className="w-4 h-4" />
                {CREDIT_COST} credit{CREDIT_COST === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleConfirmGeneration(false)}>
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
